import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import Button from "../components/Button";
import Marquee from "../components/Marquee";
import RevealOnScroll from "../components/RevealOnScroll";
import PillarCard from "../components/PillarCard";
import ComparisonTable from "../components/ComparisonTable";
import ServiceRow from "../components/ServiceRow";
import ClientLogoGrid from "../components/ClientLogoGrid";
import CaseStudyCard from "../components/CaseStudyCard";
import { PILLARS, SERVICES, MARQUEE_WORDS, WORKS, PARTNERS } from "../data/content";

const Home = () => {
  return (
    <>
      <Seo
        title="OnSports — La Impact Sports Consultancy italiana dal 2018"
        description="OnSports è la prima Impact Sports Consultancy italiana. Strategia, sponsorship, content, performance marketing per brand sportivi e property internazionali."
        path="/"
      />

      {/* HERO */}
      <section
        className="relative min-h-screen bg-dodgers text-galactico flex flex-col justify-between pt-32 md:pt-40 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/images/home/hero-poster.jpg"
          className="absolute inset-0 w-full h-full object-cover z-0"
          aria-hidden="true"
          data-testid="hero-bg-video"
        >
          <source src="/videos/home-hero.mp4" type="video/mp4" />
        </video>
        {/* Overlay per leggibilità testo */}
        <div
          className="absolute inset-0 bg-dodgers/60 z-[1] pointer-events-none"
          aria-hidden="true"
        />

        <div className="container-os flex-1 flex flex-col justify-center pb-20 relative z-10">
          <RevealOnScroll y={20}>
            <SectionEyebrow testId="hero-eyebrow">Impact Sports Consultancy</SectionEyebrow>
          </RevealOnScroll>

          <h1 className="display-h1 mt-7 max-w-[18ch]" data-testid="hero-headline">
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="block"
            >
              Non vendiamo visibilità.
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="block"
            >
              Creiamo
            </motion.span>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.55 }}
              className="block"
            >
              <span
                className="impatto-wipe"
                data-text="IMPATTO."
                aria-label="IMPATTO."
              >
                IMPATTO.
              </span>
            </motion.span>
          </h1>

          <RevealOnScroll delay={0.6} className="mt-10 max-w-[60ch]">
            <p className="body-lg opacity-85">
              OnSports è la prima Impact Sports Consultancy italiana. Trasformiamo lo
              sport da costo di comunicazione in investimento misurabile su tre
              dimensioni: <strong className="text-lime font-semibold">Business</strong>,{" "}
              <strong className="text-lime font-semibold">Cultura</strong>,{" "}
              <strong className="text-lime font-semibold">Società</strong>.
            </p>
          </RevealOnScroll>

          <RevealOnScroll delay={0.75} className="mt-10 flex flex-wrap gap-4">
            <Link to="/servizi" data-testid="hero-cta-primary">
              <Button variant="primary">Scopri come</Button>
            </Link>
            <Link to="/works" data-testid="hero-cta-secondary">
              <Button variant="secondary-light">Guarda i risultati</Button>
            </Link>
          </RevealOnScroll>
        </div>

        <Marquee items={MARQUEE_WORDS} tone="light" className="relative z-10" />
      </section>

      {/* IL PROBLEMA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-problema">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>Il contesto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Il vecchio modello <br />
                  non funziona più.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch]">
              <RevealOnScroll delay={0.15}>
                <p>
                  Per decenni, investire nello sport ha significato comprare visibilità:
                  logo sulla maglia, cartellone a bordo campo, spot durante la partita.
                  I CFO chiedevano ROI, i marketing manager rispondevano con
                  "reach" e "awareness". Numeri che non significano nulla per il board.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Oggi i tempi sono cambiati. I budget si giustificano con business case
                  solidi. I target ESG richiedono impatto sociale documentabile. Le
                  startup non possono permettersi investimenti che non generano pipeline.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  Il problema? La maggior parte degli operatori ragiona ancora per
                  compartimenti stagni. Chi fa strategia non capisce l'execution. Chi fa
                  contenuti non conosce la performance. Chi fa CSR non sa collegarla al
                  business.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* LA NOSTRA RISPOSTA */}
      <section className="section-pad bg-dodgers text-galactico relative overflow-hidden" data-testid="section-risposta">
        <div className="container-os relative">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>Un nuovo paradigma</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">L'impatto misurabile.</h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch]">
              <RevealOnScroll delay={0.15}>
                <p className="opacity-90">
                  OnSports nasce per colmare questo gap. Offriamo consulenza strategica
                  integrata che collega obiettivi di business, posizionamento culturale e
                  impatto sociale in framework operativi concreti — con KPI chiari e
                  risultati tracciabili.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p className="opacity-90">
                  Mentre il mercato dello sport parla di engagement e reach, noi
                  possediamo una parola diversa:
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.25}>
            <p
              className="font-display text-lime mt-10 md:mt-14 w-full"
              style={{
                fontSize: "clamp(48px, 13.5vw, 220px)",
                lineHeight: "0.9",
                letterSpacing: "-0.03em",
                wordBreak: "keep-all",
                overflowWrap: "normal",
                hyphens: "none",
              }}
            >
              IMPATTO.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* TRE PILASTRI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-pilastri">
        <div className="container-os">
          <div className="max-w-3xl mb-14 md:mb-20">
            <RevealOnScroll>
              <SectionEyebrow>Tre dimensioni</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <h2 className="display-h2 mt-6">Impatto misurabile su tre pilastri.</h2>
            </RevealOnScroll>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
            {PILLARS.map((p, i) => (
              <RevealOnScroll key={p.num} delay={i * 0.08}>
                <PillarCard
                  num={p.num}
                  title={p.title}
                  body={p.body}
                  tone="dark"
                  testId={`pillar-${p.num}`}
                />
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* POSIZIONAMENTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-posizionamento">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Posizionamento</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Non siamo un'agenzia.<br />
                  Siamo il vostro <span className="text-lime">partner strategico</span>.
                </h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll>
            <ComparisonTable />
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-10 md:mt-12 max-w-[68ch] body-lg opacity-90">
              Un unico partner che parla sia il linguaggio del CFO che quello del CMO.
              Che conosce lo sport dall'interno e sa tradurlo in valore di board.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* SERVIZI PREVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-servizi-preview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Cosa facciamo</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Sei aree integrate.<br />
                  Un unico obiettivo: <span className="text-lime">l'impatto</span>.
                </h2>
              </RevealOnScroll>
            </div>
          </div>
          <div>
            {SERVICES.map((s, i) => (
              <RevealOnScroll key={s.slug} delay={i * 0.04}>
                <ServiceRow
                  num={s.num}
                  title={s.title}
                  short={s.short}
                  slug={s.slug}
                  testId={`service-row-${s.num}`}
                />
              </RevealOnScroll>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/servizi" data-testid="servizi-preview-cta">
              <Button variant="secondary-dark">Esplora tutti i servizi</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CLIENTI */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-clienti">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-6 md:mb-8">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Trusted by</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Brand e property che hanno scelto l'impatto.</h2>
              </RevealOnScroll>
            </div>
          </div>

          <RevealOnScroll delay={0.1}>
            <p className="mx-auto max-w-[720px] text-center text-[18px] leading-relaxed text-galactico/80">
              Aziende leader, club di Serie A, leghe nazionali e brand internazionali che
              hanno trasformato i loro investimenti nello sport in risultati misurabili.
            </p>
          </RevealOnScroll>

          <RevealOnScroll>
            <div className="mt-8 md:mt-12">
              <ClientLogoGrid total={20} />
            </div>
          </RevealOnScroll>

          {/* PARTNER block */}
          <div className="mt-24 md:mt-32 grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Partner</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.08}>
                <h3 className="font-display mt-5 leading-[1.05] max-w-[22ch]" style={{ fontSize: "clamp(1.75rem, 3.2vw, 2.75rem)" }}>
                  L'ecosistema che amplifica l'impatto.
                </h3>
              </RevealOnScroll>
              <RevealOnScroll delay={0.14}>
                <p className="mt-5 max-w-[58ch] text-[18px] leading-relaxed text-galactico/80">
                  Non lavoriamo da soli. Collaboriamo con partner strategici che condividono
                  la nostra visione: trasformare lo sport in una leva di crescita concreta.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
              data-testid="partners-grid"
            >
              {PARTNERS.map((p, i) => (
                <div
                  key={p.name}
                  className="aspect-[4/3] sm:aspect-[5/3] relative group overflow-hidden border-r border-b border-galactico/10 first:border-l sm:[&:nth-child(2n+1)]:border-l lg:[&:nth-child(2n+1)]:border-l-0 lg:[&:nth-child(4n+1)]:border-l border-l sm:border-l-0"
                  data-testid={`partner-logo-${i}`}
                >
                  <img
                    src={p.logo}
                    alt={p.name}
                    decoding="async"
                    className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 ${
                      p.tight
                        ? "scale-[0.45] group-hover:scale-[0.52]"
                        : "scale-[1.06] group-hover:scale-[1.14]"
                    }`}
                    draggable="false"
                  />
                  <span
                    className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    aria-hidden="true"
                  />
                  <span className="sr-only">{p.name}</span>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* CASI STUDIO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-casi-studio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-14">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Impatto documentato</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Non promesse.<br />
                  <span className="text-dodgers">Risultati.</span>
                </h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WORKS.slice(0, 4).map((w, i) => (
              <RevealOnScroll key={i} delay={i * 0.07}>
                <CaseStudyCard
                  title={w.title}
                  category={w.category}
                  kpis={w.kpis}
                  tone={w.tone}
                  to={w.to || "/works"}
                  image={w.image}
                  testId={`case-study-${i}`}
                />
              </RevealOnScroll>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/works" data-testid="casi-studio-cta">
              <Button variant="secondary-dark">Tutti i casi studio</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-44 relative overflow-hidden"
        data-testid="section-cta-finale"
      >
        <div className="container-os relative">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[20ch]">
              Pronto a trasformare lo sport in{" "}
              <span className="text-lime">impatto misurabile</span>?
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[64ch]">
              Raccontaci i tuoi obiettivi. Ti mostreremo come lo sport può diventare la
              leva più potente per raggiungerli — con risultati che puoi portare in
              boardroom.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <Link to="/contatti" className="mt-10 inline-block" data-testid="cta-finale-link">
              <Button variant="primary">Parliamone</Button>
            </Link>
          </RevealOnScroll>
        </div>
      </section>
    </>
  );
};

export default Home;
